import {
    useState,
    useCallback,
    ReactElement,
    useRef,
    useEffect,
    useMemo,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Draggable,
    Droppable,
    DroppableProvided,
    DraggableProvided,
} from "@hello-pangea/dnd";
import {
    itemRename,
    itemCreateAfter,
    itemCreateBefore,
    durationChange,
    lockChange,
    remove,
    itemOrderChange,
} from "../../actions";
import { Item } from "../../model/item";
import { State } from "../../state";
import { getCurrentDay } from "../../state/currentDay.selectors";
import { h } from "../../h";
import { getItems } from "../../state/days.selectors";
import { ItemView, ItemLine } from "./ItemLine";
import { generateId } from "../../model/id";
import { getMinutesFromDate } from "../../utils/date";

const useNow = (interval = 10000): Date => {
    const [now, setNow] = useState(new Date());
    useEffect(() => {
        const intervalId = setInterval(() => {
            setNow(new Date());
        }, interval);
        return () => {
            clearInterval(intervalId);
        };
    }, [interval]);
    return now;
};

export const Items = (): ReactElement => {
    const dispatch = useDispatch();
    const now = useNow();
    const currentDay = useSelector(getCurrentDay);
    const nowMinutes = getMinutesFromDate(now, currentDay);
    const items =
        useSelector((state: State) => getItems(state, currentDay)) || [];
    const inputRefs = useRef<{ [id: number]: HTMLInputElement }>({});
    const [focusId, setFocusId] = useState<number>();
    const itemsIdsNew = items.map(({ id }) => id);
    const itemsIds = useMemo(() => itemsIdsNew, [itemsIdsNew.join(`,`)]); //eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (items.length) {
            setFocusId(items[items.length - 1].id);
        }
    }, [currentDay, items.length === 0]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (focusId != null) {
            const ref = inputRefs.current[focusId];
            if (ref) {
                ref.focus();
            }
        }
    }, [focusId]);
    const itemsView = items
        .reduce(
            (dataWithTime, item) => {
                if (item.lock) {
                    dataWithTime.push({ ...item, time: item.lock });
                    return dataWithTime;
                }
                const previousItem = dataWithTime[dataWithTime.length - 1];
                dataWithTime.push({
                    ...item,
                    time: previousItem.time + previousItem.duration,
                });
                return dataWithTime;
            },
            [] as Array<
                Item & {
                    time: number;
                }
            >,
        )
        .reduceRight((newData, item) => {
            const nextLock = newData.find(({ lock }) => lock);
            if (nextLock && nextLock.lock) {
                if (nextLock.lock < item.time + item.duration) {
                    newData.unshift({ ...item, isWarning: true });
                    return newData;
                }
            }
            newData.unshift(item);
            return newData;
        }, [] as Array<ItemView>);
    useEffect(() => {
        inputRefs.current = itemsIds.reduce((result, id) => {
            result[id] = inputRefs.current[id];
            return result;
        }, {} as { [id: number]: HTMLInputElement });
    }, [itemsIds, currentDay]);
    const onTitleChange = useCallback(
        (index: number, updatedItem: Item) => {
            dispatch(itemRename(currentDay, index, updatedItem));
        },
        [dispatch, currentDay],
    );
    const onCreateAfter = useCallback(
        (index) => {
            const id = generateId();
            dispatch(
                itemCreateAfter(currentDay, index, {
                    id,
                    title: ``,
                    duration: 30,
                    lock: null,
                }),
            );
            setFocusId(id);
        },
        [dispatch, currentDay],
    );
    const onCreateBefore = useCallback(
        (index) => {
            const id = generateId();
            dispatch(
                itemCreateBefore(currentDay, index, {
                    id,
                    title: ``,
                    duration: 30,
                    lock: null,
                }),
            );
            setFocusId(id);
        },
        [dispatch, currentDay],
    );
    const onDurationChange = useCallback(
        (index, updatedItem) => {
            dispatch(durationChange(currentDay, index, updatedItem));
        },
        [dispatch, currentDay],
    );
    const onLockChange = useCallback(
        (index, updatedItem) => {
            dispatch(lockChange(currentDay, index, updatedItem));
        },
        [dispatch, currentDay],
    );
    const onRemove = useCallback(
        (index) => {
            dispatch(remove(currentDay, index));
            setFocusId(itemsIds[index === 0 ? 1 : Math.max(0, index - 1)]);
        },
        [dispatch, currentDay, itemsIds],
    );
    const onFocusPrevious = useCallback(
        (index) => {
            setFocusId(itemsIds[Math.max(0, index - 1)]);
        },
        [itemsIds],
    );
    const onFocusNext = useCallback(
        (index) => {
            setFocusId(itemsIds[Math.min(itemsIds.length - 1, index + 1)]);
        },
        [itemsIds],
    );
    const onFocusItem = useCallback(
        (index) => {
            setFocusId(itemsIds[index]);
        },
        [itemsIds],
    );
    const onOrderChange = useCallback(
        (from, to) => {
            if (to < 0 || itemsIds.length <= to) {
                return;
            }
            dispatch(itemOrderChange({ date: currentDay, from, to }));
        },
        [dispatch, currentDay, itemsIds],
    );
    const inputRef = useCallback(
        (inputElement: HTMLInputElement, id: number): void => {
            if (!inputElement) {
                return;
            }
            if (id === focusId) {
                inputElement.focus();
            }
            inputRefs.current[id] = inputElement;
        },
        [focusId],
    );
    return h(Droppable, {
        droppableId: `items`,
        children: ({
            innerRef,
            droppableProps,
            placeholder,
        }: DroppableProvided): React.ReactElement<any> =>
            h(
                `div`,
                {
                    ref: innerRef,
                    ...droppableProps,
                },
                itemsView.map((item, index) =>
                    h(Draggable, {
                        key: item.id,
                        draggableId: `` + item.id,
                        index,
                        children: ({
                            innerRef,
                            draggableProps,
                            dragHandleProps,
                        }: DraggableProvided): any =>
                            h(ItemLine, {
                                item: items[index],
                                innerRef,
                                inputRef,
                                draggableProps,
                                dragHandleProps,
                                index,
                                onTitleChange,
                                onCreateAfter,
                                onCreateBefore,
                                onDurationChange,
                                onLockChange,
                                onRemove,
                                onOrderChange,
                                onFocusItem,
                                onFocusPrevious,
                                onFocusNext,
                                isWarning: item.isWarning,
                                time: item.time,
                                isPast: item.time + item.duration < nowMinutes,
                                isCurrent:
                                    item.time <= nowMinutes &&
                                    nowMinutes < item.time + item.duration,
                                isFuture: nowMinutes < item.time,
                            }),
                    }),
                ),
                placeholder,
            ),
    });
};
